import { Box, Button, Heading, HStack, Text, VStack } from "components/ui";
import { toaster } from "components/ui/toaster";
import { useTranslation } from "hooks/useTranslation";
import { FaArrowRight, FaCheck, FaRocket, FaServer } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { getThemeColor } from "theme/colors";

interface Step6SuccessProps {
  serverId: string;
  serverName: string;
  widgetId: string;
  toolCount: number;
  onComplete: () => void;
}

export const Step6Success = ({
  serverId,
  serverName,
  widgetId,
  toolCount,
  onComplete,
}: Step6SuccessProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleComplete = () => {
    toaster.create({
      title: t("app.onboarding.step6.welcomeToast"),
      description: t("app.onboarding.step6.workspaceReady"),
      type: "success",
    });

    onComplete();
  };

  return (
    <Box position="relative" h="full" display="flex" flexDirection="column">
      {/* Scrollable Content */}
      <Box flex="1" overflowY="auto" px={6} py={8}>
        <VStack gap={6} align="stretch" maxW="600px" mx="auto">
          {/* Success Animation */}
          <Box textAlign="center" py={4}>
            <Heading
              size="2xl"
              mb={3}
              color={getThemeColor("text.primary")}
              fontWeight="bold"
              letterSpacing="tight"
            >
              {t("app.onboarding.step6.title")}
            </Heading>
            <Text
              fontSize="lg"
              color={getThemeColor("text.secondary")}
              fontWeight="medium"
            >
              {t("app.onboarding.step6.subtitle")}
            </Text>
          </Box>

          {/* Next Steps */}
          <Box
            borderRadius="xl"
            bg={getThemeColor("alert.info.bg")}
            p={5}
            borderWidth="1px"
            borderColor={getThemeColor("alert.info.border")}
          >
            <HStack gap={2} mb={4}>
              <Box
                w="32px"
                h="32px"
                borderRadius="lg"
                bg={getThemeColor("bg.tertiary")}
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <FaRocket size={16} color="#3b82f6" />
              </Box>
              <Heading size="sm" color={getThemeColor("alert.info.text")}>
                {t("app.onboarding.step6.whatsNext")}
              </Heading>
            </HStack>
            <VStack align="start" gap={3}>
              <HStack gap={3} align="start">
                <Box
                  w="20px"
                  h="20px"
                  borderRadius="md"
                  bg="blue.500"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  flexShrink={0}
                  mt={0.5}
                >
                  <FaCheck size={12} color="white" />
                </Box>
                <Text fontSize="sm" color={getThemeColor("text.primary")}>
                  {t("app.onboarding.step6.embedWidget")}
                </Text>
              </HStack>
              <HStack gap={3} align="start">
                <Box
                  w="20px"
                  h="20px"
                  borderRadius="md"
                  bg="blue.500"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  flexShrink={0}
                  mt={0.5}
                >
                  <FaCheck size={12} color="white" />
                </Box>
                <Text fontSize="sm" color={getThemeColor("text.primary")}>
                  {t("app.onboarding.step6.monitorConversations")}
                </Text>
              </HStack>
              <HStack gap={3} align="start">
                <Box
                  w="20px"
                  h="20px"
                  borderRadius="md"
                  bg="blue.500"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  flexShrink={0}
                  mt={0.5}
                >
                  <FaCheck size={12} color="white" />
                </Box>
                <Text fontSize="sm" color={getThemeColor("text.primary")}>
                  {t("app.onboarding.step6.createServers")}
                </Text>
              </HStack>
            </VStack>
          </Box>

          {/* Quick Actions */}
          <VStack gap={3} align="stretch">
            <Heading size="sm" color={getThemeColor("text.primary")} mb={1}>
              {t("app.onboarding.step6.quickActions")}
            </Heading>

            <Button
              size="lg"
              variant="outline"
              justifyContent="space-between"
              onClick={() => {
                onComplete();
                navigate(`/admin/servers?serverId=${serverId}`);
              }}
              borderRadius="xl"
              borderWidth="2px"
              borderColor={getThemeColor("border.default")}
              bg={getThemeColor("bg.primary")}
              _hover={{
                borderColor: "teal.400",
                bg: getThemeColor("bg.hover"),
              }}
              h="auto"
              py={4}
              px={5}
            >
              <HStack gap={3} flex={1}>
                <Box
                  w="40px"
                  h="40px"
                  borderRadius="lg"
                  bg={getThemeColor("bg.tertiary")}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  <FaServer size={20} color="#14b8a6" />
                </Box>
                <VStack align="start" gap={0.5}>
                  <Text
                    fontWeight="semibold"
                    fontSize="md"
                    color={getThemeColor("text.primary")}
                  >
                    {serverName}
                  </Text>
                  <Text fontSize="xs" color={getThemeColor("text.secondary")}>
                    {t("app.onboarding.step6.viewServerDetails")}
                  </Text>
                </VStack>
              </HStack>
              <FaArrowRight color="gray.400" />
            </Button>

            <Button
              size="lg"
              variant="outline"
              justifyContent="space-between"
              onClick={() => {
                onComplete();
                navigate(`/admin/widgets/${widgetId}`);
              }}
              borderRadius="xl"
              borderWidth="2px"
              borderColor={getThemeColor("border.default")}
              bg={getThemeColor("bg.primary")}
              _hover={{
                borderColor: "purple.400",
                bg: getThemeColor("bg.hover"),
              }}
              h="auto"
              py={4}
              px={5}
            >
              <HStack gap={3} flex={1}>
                <Box
                  w="40px"
                  h="40px"
                  borderRadius="lg"
                  bg={getThemeColor("bg.tertiary")}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  <Text fontSize="2xl">💬</Text>
                </Box>
                <VStack align="start" gap={0.5}>
                  <Text
                    fontWeight="semibold"
                    fontSize="md"
                    color={getThemeColor("text.primary")}
                  >
                    {t("app.onboarding.step6.customizeWidget")}
                  </Text>
                  <Text fontSize="xs" color={getThemeColor("text.secondary")}>
                    {t("app.onboarding.step6.customizeWidgetDesc")}
                  </Text>
                </VStack>
              </HStack>
              <FaArrowRight color="gray.400" />
            </Button>
          </VStack>
        </VStack>
      </Box>

      {/* Sticky Footer */}
      <Box
        position="sticky"
        bottom={0}
        borderTopWidth="1px"
        borderColor={getThemeColor("border.default")}
        bg={getThemeColor("modal.bg")}
        py={4}
        px={6}
      >
        <HStack justify="center">
          <Button
            size="lg"
            colorPalette="blue"
            onClick={handleComplete}
            borderRadius="xl"
            px={8}
            h="52px"
            fontSize="md"
            fontWeight="semibold"
            boxShadow="0 4px 12px -2px rgba(59, 130, 246, 0.3)"
            _hover={{
              boxShadow: "0 6px 16px -2px rgba(59, 130, 246, 0.4)",
              transform: "translateY(-2px)",
            }}
            transition="all 0.2s"
          >
            {t("app.onboarding.step6.finishSetup")}
            <Box ml={2}>
              <FaArrowRight />
            </Box>
          </Button>
        </HStack>
      </Box>
    </Box>
  );
};
